import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { CalendarClock, RefreshCw, AlertCircle } from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/Card';
import { getExpiries } from '../../services/referenceDataService';
import { UNDERLYING } from '../../constants/tradingConstants';
import { isWhiteLabel } from '../../config/tenantConfig';
import { cn } from '../../utils/cn';
import type { Expiry } from '../../types/expiry';

type Underlying = keyof typeof UNDERLYING;
const ALL_UNDERLYINGS: Underlying[] = Object.keys(UNDERLYING) as Underlying[];

const fmtDate = (iso: string): string => {
  if (!iso) return '—';
  const d = new Date(iso + 'T00:00:00');
  return d.toLocaleDateString('en-IN', { weekday: 'short', day: 'numeric', month: 'short', year: 'numeric' });
};

const daysFromToday = (iso: string): number => {
  const d = new Date(iso + 'T00:00:00');
  const today = new Date();
  today.setHours(0, 0, 0, 0);
  return Math.round((d.getTime() - today.getTime()) / 86400000);
};

const ExpiryDaysSection: React.FC = () => {
  const [index, setIndex] = useState<Underlying>('NIFTY');
  const [expiries, setExpiries] = useState<Expiry[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const wl = isWhiteLabel();
  const accent = wl ? 'bg-zebu-600 text-white' : 'bg-blue-600 text-white';

  const fetchExpiries = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      // Always request `weekly` — the backend includes monthly expiries in the
      // weekly response (monthlies are flagged via `is_monthly`). For indices
      // that have no weekly contracts (BANKNIFTY / FINNIFTY / MIDCPNIFTY /
      // BANKEX) this still returns the monthly series, so one call covers
      // every index without branching.
      const r = await getExpiries(UNDERLYING[index], 'weekly', 12);
      setExpiries(r.expiries);
    } catch (e: any) {
      setError(e?.message || 'Failed to load expiries.');
      setExpiries([]);
    } finally {
      setLoading(false);
    }
  }, [index]);

  useEffect(() => { fetchExpiries(); }, [fetchExpiries]);

  // Drop past expiries (DTE < 0) — users only care about what's still trading.
  // The "current weekly contract" is the nearest upcoming expiry of ANY type
  // (since a monthly expiry IS also the active weekly contract on its day),
  // while the "current monthly contract" is the nearest is_monthly=true row.
  // When these coincide (monthly expiry day on an index that has weeklies),
  // a single row carries both flags and renders both pills.
  // For monthly-only indices (BANKNIFTY / FINNIFTY / MIDCPNIFTY / BANKEX),
  // we suppress the weekly pill since no separate weekly contract exists.
  const { rows, currentWeeklyDate, currentMonthlyDate } = useMemo(() => {
    const upcoming = expiries.filter((e) => daysFromToday(e.expiryDate) >= 0);
    const hasWeekly = upcoming.some((e) => !e.isMonthly);
    const firstAny = upcoming[0]?.expiryDate ?? null;
    const firstMonthly = upcoming.find((e) => e.isMonthly)?.expiryDate ?? null;
    return {
      rows: upcoming,
      // Null for monthly-only indices — suppresses the weekly pill.
      currentWeeklyDate: hasWeekly ? firstAny : null,
      currentMonthlyDate: firstMonthly,
    };
  }, [expiries]);

  return (
    <Card>
      <CardHeader>
        <div className="flex items-center justify-between flex-wrap gap-3">
          <CardTitle>
            <span className="inline-flex items-center gap-2">
              <CalendarClock className="w-4 h-4" />
              Expiry Days ({rows.length})
            </span>
          </CardTitle>
          <button
            onClick={fetchExpiries}
            disabled={loading}
            className="inline-flex items-center gap-1.5 px-3 py-1.5 text-sm font-medium rounded-md bg-white dark:bg-gray-800 border border-gray-300 dark:border-gray-600 text-gray-700 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-700 disabled:opacity-50"
          >
            <RefreshCw className={cn('w-3.5 h-3.5', loading && 'animate-spin')} />
            Refresh
          </button>
        </div>
      </CardHeader>
      <CardContent className="p-0">
        {/* Filter bar — index picker only. Both weekly and monthly expiries
            are always shown in one merged list. */}
        <div className="px-6 py-4 border-b border-gray-200 dark:border-gray-700 flex flex-wrap items-center gap-2">
          <div className="flex flex-wrap gap-1">
            {ALL_UNDERLYINGS.map((u) => (
              <button
                key={u}
                onClick={() => setIndex(u)}
                className={cn(
                  'px-3 py-1.5 text-xs font-medium rounded-md transition-colors',
                  index === u
                    ? accent
                    : 'bg-white dark:bg-gray-800 border border-gray-300 dark:border-gray-600 text-gray-700 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-700',
                )}
              >
                {UNDERLYING[u]}
              </button>
            ))}
          </div>
        </div>

        {/* Body */}
        {error ? (
          <div className="px-6 py-8 flex items-center justify-center gap-2 text-sm text-red-700 dark:text-red-300">
            <AlertCircle className="w-4 h-4" />
            {error}
          </div>
        ) : loading && rows.length === 0 ? (
          <div className="px-6 py-12 flex items-center justify-center text-sm text-gray-500 dark:text-gray-400">
            <RefreshCw className="w-4 h-4 animate-spin mr-2" />
            Loading expiries…
          </div>
        ) : rows.length === 0 ? (
          <div className="px-6 py-12 text-center text-sm text-gray-500 dark:text-gray-400">
            No upcoming expiries available for {UNDERLYING[index]}.
          </div>
        ) : (
          <>
            {/* Mobile (<md): card list. Each expiry is a self-contained card
                that doesn't need horizontal scrolling and shows every metric
                without truncation. */}
            <ul className="md:hidden divide-y divide-gray-200 dark:divide-gray-700">
              {rows.map((e) => {
                const dte = daysFromToday(e.expiryDate);
                const isCurrentWeekly = currentWeeklyDate !== null && e.expiryDate === currentWeeklyDate;
                const isCurrentMonthly = e.expiryDate === currentMonthlyDate;
                return (
                  <li
                    key={e.expiryDate}
                    className={cn(
                      'px-4 py-3',
                      isCurrentMonthly && 'bg-purple-50/60 dark:bg-purple-900/20 border-l-2 border-purple-500',
                      isCurrentWeekly && !isCurrentMonthly && 'bg-blue-50/60 dark:bg-blue-900/20 border-l-2 border-blue-500',
                    )}
                  >
                    {/* Header row: date + Current pills + Weekly/Monthly type badge */}
                    <div className="flex items-start justify-between gap-2 flex-wrap">
                      <div className="min-w-0 flex-1">
                        <div className={cn(
                          'text-sm text-gray-900 dark:text-white tabular-nums',
                          (isCurrentWeekly || isCurrentMonthly) ? 'font-semibold' : 'font-medium',
                        )}>
                          {fmtDate(e.expiryDate)}
                        </div>
                        {(isCurrentWeekly || isCurrentMonthly) && (
                          <div className="flex flex-wrap items-center gap-1.5 mt-1">
                            {isCurrentWeekly && (
                              <span
                                className="inline-block px-1.5 py-0.5 rounded text-[10px] font-semibold uppercase tracking-wider bg-blue-600 text-white"
                                title="Current Weekly contract"
                              >
                                Current Weekly
                              </span>
                            )}
                            {isCurrentMonthly && (
                              <span
                                className="inline-block px-1.5 py-0.5 rounded text-[10px] font-semibold uppercase tracking-wider bg-purple-600 text-white"
                                title="Current Monthly contract"
                              >
                                Current Monthly
                              </span>
                            )}
                          </div>
                        )}
                      </div>
                      <span className={cn(
                        'shrink-0 inline-block px-2 py-0.5 rounded text-xs font-semibold uppercase tracking-wider',
                        e.isMonthly
                          ? 'bg-purple-50 dark:bg-purple-900/20 text-purple-700 dark:text-purple-300'
                          : 'bg-blue-50 dark:bg-blue-900/20 text-blue-700 dark:text-blue-300',
                      )}>
                        {e.isMonthly ? 'Monthly' : 'Weekly'}
                      </span>
                    </div>

                    {/* Metric grid: Lot Size · Freeze Qty · DTE */}
                    <dl className="mt-3 grid grid-cols-3 gap-2 text-xs">
                      <div>
                        <dt className="text-gray-500 dark:text-gray-400 uppercase tracking-wider">Lot Size</dt>
                        <dd className="mt-0.5 text-sm font-medium text-gray-900 dark:text-white tabular-nums">
                          {e.lotSize.toLocaleString('en-IN')}
                        </dd>
                      </div>
                      <div>
                        <dt className="text-gray-500 dark:text-gray-400 uppercase tracking-wider">Freeze Qty</dt>
                        <dd className="mt-0.5 text-sm font-medium text-gray-900 dark:text-white tabular-nums">
                          {e.freezeLimit ? e.freezeLimit.toLocaleString('en-IN') : '—'}
                        </dd>
                      </div>
                      <div>
                        <dt className="text-gray-500 dark:text-gray-400 uppercase tracking-wider">DTE</dt>
                        <dd className="mt-0.5 text-sm font-medium text-gray-900 dark:text-white tabular-nums">
                          {dte === 0 ? 'Today' : `${dte} day${dte === 1 ? '' : 's'}`}
                        </dd>
                      </div>
                    </dl>
                  </li>
                );
              })}
            </ul>

            {/* Desktop (md+): table */}
            <div className="hidden md:block overflow-x-auto">
              <table className="min-w-full divide-y divide-gray-200 dark:divide-gray-700">
                <thead className="bg-gray-50 dark:bg-gray-700">
                  <tr>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider">Expiry Date</th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider">Type</th>
                    <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider">Lot Size</th>
                    <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider">Freeze Qty</th>
                    <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider">DTE</th>
                  </tr>
                </thead>
                <tbody className="bg-white dark:bg-gray-800 divide-y divide-gray-200 dark:divide-gray-700">
                  {rows.map((e) => {
                    const dte = daysFromToday(e.expiryDate);
                    const isCurrentWeekly = currentWeeklyDate !== null && e.expiryDate === currentWeeklyDate;
                    const isCurrentMonthly = e.expiryDate === currentMonthlyDate;
                    const isCurrent = isCurrentWeekly || isCurrentMonthly;
                    return (
                      <tr
                        key={e.expiryDate}
                        className={cn(
                          'hover:bg-gray-50 dark:hover:bg-gray-700',
                          isCurrentMonthly && 'bg-purple-50/60 dark:bg-purple-900/20 border-l-2 border-purple-500',
                          isCurrentWeekly && !isCurrentMonthly && 'bg-blue-50/60 dark:bg-blue-900/20 border-l-2 border-blue-500',
                        )}
                      >
                        <td className={cn(
                          'px-6 py-3 text-sm text-gray-900 dark:text-white tabular-nums whitespace-nowrap',
                          isCurrent ? 'font-semibold' : 'font-medium',
                        )}>
                          <div className="inline-flex items-center gap-1.5">
                            <span>{fmtDate(e.expiryDate)}</span>
                            {isCurrentWeekly && (
                              <span
                                className="inline-block px-1.5 py-0.5 rounded text-[10px] font-semibold uppercase tracking-wider bg-blue-600 text-white"
                                title="Current Weekly contract"
                              >
                                Curr · W
                              </span>
                            )}
                            {isCurrentMonthly && (
                              <span
                                className="inline-block px-1.5 py-0.5 rounded text-[10px] font-semibold uppercase tracking-wider bg-purple-600 text-white"
                                title="Current Monthly contract"
                              >
                                Curr · M
                              </span>
                            )}
                          </div>
                        </td>
                        <td className="px-6 py-3 whitespace-nowrap">
                          <span className={cn(
                            'inline-block px-2 py-0.5 rounded text-xs font-semibold uppercase tracking-wider',
                            e.isMonthly
                              ? 'bg-purple-50 dark:bg-purple-900/20 text-purple-700 dark:text-purple-300'
                              : 'bg-blue-50 dark:bg-blue-900/20 text-blue-700 dark:text-blue-300',
                          )}>
                            {e.isMonthly ? 'Monthly' : 'Weekly'}
                          </span>
                        </td>
                        <td className="px-6 py-3 text-right text-sm text-gray-700 dark:text-gray-300 tabular-nums">
                          {e.lotSize.toLocaleString('en-IN')}
                        </td>
                        <td className="px-6 py-3 text-right text-sm text-gray-700 dark:text-gray-300 tabular-nums">
                          {e.freezeLimit ? e.freezeLimit.toLocaleString('en-IN') : '—'}
                        </td>
                        <td className="px-6 py-3 text-right text-sm text-gray-700 dark:text-gray-300 tabular-nums">
                          {dte === 0 ? 'Today' : `${dte} day${dte === 1 ? '' : 's'}`}
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          </>
        )}
      </CardContent>
    </Card>
  );
};

export default ExpiryDaysSection;
